import dynamic from 'next/dynamic';
import axios from 'axios';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useIsMount } from '../../hooks/useIsMount';
import { useObjectsProvider } from '../../context/objects';
import { useLocationState } from '../../context/location';
import { declOfNum, JSONToQuery, sleep } from '../../helpers';
import Radio from '../Radio';
import Input from '../Input';
import Checkbox from '../Checkbox';
import Select from '../Select';
import Button from '../Button';

import css from '../../styles/modules/objects/ObjectsFilterMobile.module.scss';

const Modal = dynamic(() => import('../Modal'));

const isAnyFilters = (s) => {
  const arrFilters = [
    'priceFrom',
    'priceTo',
    'rooms',
    'studio',
    'squareFrom',
    'squareTo',
    'mikroraion',
    'noCommission',
    'exclusive',
    'squareAreaFrom',
    'squareAreaTo',
    'commercialType',
    'squareLivingFrom',
    'squareLivingTo',
    'squareKitchenFrom',
    'squareKitchenTo',
    'floorFrom',
    'floorTo',
    'floorsFrom',
    'floorsTo',
    'ceiling',
    'balcony',
    'renovation',
    'bathroom',
    'buildingType',
    'stock',
    'lotType',
    'subCategory',
    'entrance',
    'commercialBuildingType'
  ];
  let filtersTotal = 0;

  for (let i = 0; i < arrFilters.length; i++)
    if (
      s[arrFilters[i]] &&
      (s[arrFilters[i]].length || s[arrFilters[i]] > 0)
    )
      filtersTotal++;

  return filtersTotal > 0;
};

const ObjectsFilterMobile = ({ isMap }) => {
  /** STATES */
  const router = useRouter();
  const isMount = useIsMount();
  const locationState = useLocationState();
  const [objectsState, setObjectsState] = useObjectsProvider();
  const [foundCount, setFoundCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [type, setType] = useState(objectsState.filter.type);
  const [category, setCategory] = useState(objectsState.filter.category);
  const [priceFrom, setPriceFrom] = useState(objectsState.filter.priceFrom);
  const [priceTo, setPriceTo] = useState(objectsState.filter.priceTo);
  const [noCommission, setNoCommission] = useState(objectsState.filter.noCommission);
  const [exclusive, setExclusive] = useState(objectsState.filter.exclusive);
  const [district, setDistrict] = useState(objectsState.filter.mikroraion);
  const [studio, setStudio] = useState(objectsState.filter.studio);
  const [rooms, setRooms] = useState(objectsState.filter.rooms);
  const [squareFrom, setSquareFrom] = useState(objectsState.filter.squareFrom);
  const [squareTo, setSquareTo] = useState(objectsState.filter.squareTo);
  const [squareAreaFrom, setSquareAreaFrom] = useState(objectsState.filter.squareAreaFrom);
  const [squareAreaTo, setSquareAreaTo] = useState(objectsState.filter.squareAreaTo);
  const [commercialType, setCommercialType] = useState(objectsState.filter.commercialType);
  const [squareLivingFrom, setSquareLivingFrom] = useState(objectsState.filter.squareLivingFrom);
  const [squareLivingTo, setSquareLivingTo] = useState(objectsState.filter.squareLivingTo);
  const [squareKitchenFrom, setSquareKitchenFrom] = useState(objectsState.filter.squareKitchenFrom);
  const [squareKitchenTo, setSquareKitchenTo] = useState(objectsState.filter.squareKitchenTo);
  const [balcony, setBalcony] = useState(objectsState.filter.balcony);
  const [ceiling, setCeiling] = useState(objectsState.filter.ceiling);
  const [floorFrom, setFloorFrom] = useState(objectsState.filter.floorFrom);
  const [floorTo, setFloorTo] = useState(objectsState.filter.floorTo);
  const [floorsFrom, setFloorsFrom] = useState(objectsState.filter.floorsFrom);
  const [floorsTo, setFloorsTo] = useState(objectsState.filter.floorsTo);
  const [renovation, setRenovation] = useState(objectsState.filter.renovation);
  const [buildingType, setBuildingType] = useState(objectsState.filter.buildingType);
  const [bathroom, setBathroom] = useState(objectsState.filter.bathroom);
  const [stock, setStock] = useState(objectsState.filter.stock);
  const [lotType, setLotType] = useState(objectsState.filter.lotType);
  const [subCategory, setSubCategory] = useState(objectsState.filter.subCategory);
  const [entrance, setEntrance] = useState(objectsState.filter.entrance);
  const [commercialBuildingType, setCommercialBuildingType] = useState(objectsState.filter.commercialBuildingType);

  /** FUNCTIONS */
  const handleSearchClick = () => {
    const newFilterSearch = { ...objectsState.filter };

    newFilterSearch.type = type;
    newFilterSearch.category = category
    newFilterSearch.priceFrom = priceFrom;
    newFilterSearch.priceTo = priceTo;
    newFilterSearch.studio = ['квартира'].includes(category) ? studio : 0;
    newFilterSearch.rooms = ['квартира'].includes(category) ? rooms : [];
    newFilterSearch.squareFrom = ['квартира', 'комната', 'дом'].includes(category) ? squareFrom : '';
    newFilterSearch.squareTo = ['квартира', 'комната', 'дом'].includes(category) ? squareTo : '';
    newFilterSearch.squareAreaFrom = ['дом', 'участок'].includes(category) ? squareAreaFrom : '';
    newFilterSearch.squareAreaTo = ['дом', 'участок'].includes(category) ? squareAreaTo : '';
    newFilterSearch.commercialType = ['коммерция'].includes(category) ? commercialType : [];
    newFilterSearch.mikroraion = district;
    newFilterSearch.noCommission = noCommission;
    newFilterSearch.exclusive = exclusive;
    newFilterSearch.squareLivingFrom = ['квартира', 'комната', 'дом'].includes(category) ? squareLivingFrom : '';
    newFilterSearch.squareLivingTo = ['квартира', 'комната', 'дом'].includes(category) ? squareLivingTo : '';
    newFilterSearch.squareKitchenFrom = ['квартира', 'дом'].includes(category) ? squareKitchenFrom : '';
    newFilterSearch.squareKitchenTo = ['квартира', 'дом'].includes(category) ? squareKitchenTo : '';
    newFilterSearch.floorFrom = ['квартира', 'комната', 'коммерция'].includes(category) ? floorFrom : '';
    newFilterSearch.floorTo = ['квартира', 'комната', 'коммерция'].includes(category) ? floorTo : '';
    newFilterSearch.floorsFrom = ['квартира', 'комната', 'дом', 'коммерция'].includes(category) ? floorsFrom : '';
    newFilterSearch.floorsTo = ['квартира', 'комната', 'дом', 'коммерция'].includes(category) ? floorsTo : '';
    newFilterSearch.ceiling = ['квартира', 'комната', 'дом', 'коммерция'].includes(category) ? ceiling : '';
    newFilterSearch.balcony = ['квартира', 'комната'].includes(category) ? balcony : [];
    newFilterSearch.renovation = ['квартира', 'комната', 'дом', 'коммерция'].includes(category) ? renovation : [];
    newFilterSearch.bathroom = ['квартира', 'комната'].includes(category) ? bathroom : [];
    newFilterSearch.buildingType = ['квартира', 'комната', 'дом'].includes(category) ? buildingType : [];
    newFilterSearch.stock = ['квартира', 'комната'].includes(category) ? stock : [];
    newFilterSearch.lotType = ['дом', 'участок'].includes(category) ? lotType : [];
    newFilterSearch.subCategory = ['дом'].includes(category) ? subCategory : [];
    newFilterSearch.entrance = ['коммерция'].includes(category) ? entrance : [];
    newFilterSearch.commercialBuildingType = ['коммерция'].includes(category) ? commercialBuildingType : [];
    newFilterSearch.ads = [];

    setObjectsState({ type: 'SET_PAGINATION', payload: {} });
    setObjectsState({ type: 'SET_FILTER', payload: newFilterSearch });
    setObjectsState({
      type: 'SET_STATE',
      payload: {
        ...objectsState.state,
        isFiltersMobileVisible: false,
        isMapCatalogVisible: false
      }
    });

    if (isMap)
      router.replace(`/${router.query.region}/objects/map${JSONToQuery({ ...newFilterSearch, ...objectsState.map })}`);
  };
  const handleResetClick = () => {
    setPriceFrom('');
    setPriceTo('');
    setNoCommission(0);
    setExclusive(0);
    setDistrict([]);
    setStudio(0);
    setRooms([]);
    setSquareFrom('');
    setSquareTo('');
    setSquareAreaFrom('');
    setSquareAreaTo('');
    setCommercialType([]);
    setSquareLivingFrom('');
    setSquareLivingTo('');
    setSquareKitchenFrom('');
    setSquareKitchenTo('');
    setBalcony([]);
    setCeiling('');
    setFloorFrom('');
    setFloorTo('');
    setFloorsFrom('');
    setFloorsTo('');
    setRenovation([]);
    setBuildingType([]);
    setBathroom([]);
    setStock([]);
    setLotType([]);
    setSubCategory([]);
    setEntrance([]);
    setCommercialBuildingType([]);

    setObjectsState({ type: 'SET_PAGINATION', payload: {} });
    setObjectsState({ type: 'SET_FILTER', payload: { type, category } });
    setObjectsState({
      type: 'SET_STATE',
      payload: {
        ...objectsState.state,
        isFiltersMobileVisible: false,
        isMapCatalogVisible: false
      }
    });

    if (isMap)
      router.replace(`/${router.query.region}/objects/map`);
  };

  /** HOOKS */
  useEffect(() => {
    if (isMount || isMap) return;

    let newLink = ['/', router.query.region, '/objects'].join('');

    switch (objectsState.filter.type) {
      default:
      case 'продажа':
        newLink += '/buy';
        break;

      case 'аренда':
        newLink += '/rent';
        break;
    }
    switch (objectsState.filter.category) {
      default:
      case 'квартира':
        newLink += '/flats';
        break;

      case 'комната':
        newLink += '/rooms';
        break;

      case 'дом':
        newLink += '/houses';
        break;

      case 'участок':
        newLink += '/sites';
        break;

      case 'коммерция':
        newLink += '/commercial';
        break;
    }
    if (objectsState.filter.category === 'квартира') {
      if (objectsState.filter.rooms.length === 0 && objectsState.filter.studio)
        newLink += '/studio';

      if (objectsState.filter.rooms.length === 1 && !objectsState.filter.studio)
        newLink += `/${objectsState.filter.rooms[0]}k`;
    }

    router.replace(
      `${newLink}${JSONToQuery(
        { ...objectsState.filter, ...objectsState.pagination, ...objectsState.sorting },
        [
          'type',
          'category',
          'length',
          objectsState.filter.studio && !objectsState.filter.rooms.length && 'studio',
          !objectsState.filter.studio && objectsState.filter.rooms.length === 1 && 'rooms'
        ],
        ['page=1', 'sort=last-update.date']
      )}`
    );
  }, [objectsState.filter, objectsState.pagination]);
  useEffect(() => {
    if (!locationState.isLoaded)
      return;

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      setIsLoading(true);
      await sleep(300);

      try {
        const response = await axios.post(process.env.NEXT_PUBLIC_API + '/objects', {
          type,
          category,
          priceFrom,
          priceTo,
          studio: ['квартира'].includes(category) ? studio : 0,
          rooms: ['квартира'].includes(category) ? rooms : [],
          squareFrom: ['квартира', 'комната', 'дом'].includes(category) ? squareFrom : '',
          squareTo: ['квартира', 'комната', 'дом'].includes(category) ? squareTo : '',
          squareAreaFrom: ['дом', 'участок'].includes(category) ? squareAreaFrom : '',
          squareAreaTo: ['дом', 'участок'].includes(category) ? squareAreaTo : '',
          commercialType: ['коммерция'].includes(category) ? commercialType : [],
          mikroraion: district,
          noCommission: noCommission,
          exclusive: exclusive,
          squareLivingFrom: ['квартира', 'комната', 'дом'].includes(category) ? squareLivingFrom : '',
          squareLivingTo: ['квартира', 'комната', 'дом'].includes(category) ? squareLivingTo : '',
          squareKitchenFrom: ['квартира', 'дом'].includes(category) ? squareKitchenFrom : '',
          squareKitchenTo: ['квартира', 'дом'].includes(category) ? squareKitchenTo : '',
          floorFrom: ['квартира', 'комната', 'коммерция'].includes(category) ? floorFrom : '',
          floorTo: ['квартира', 'комната', 'коммерция'].includes(category) ? floorTo : '',
          floorsFrom: ['квартира', 'комната', 'дом', 'коммерция'].includes(category) ? floorsFrom : '',
          floorsTo: ['квартира', 'комната', 'дом', 'коммерция'].includes(category) ? floorsTo : '',
          ceiling: ['квартира', 'комната', 'дом', 'коммерция'].includes(category) ? ceiling : '',
          balcony: ['квартира', 'комната'].includes(category) ? balcony : [],
          renovation: ['квартира', 'комната', 'дом', 'коммерция'].includes(category) ? renovation : [],
          bathroom: ['квартира', 'комната'].includes(category) ? bathroom : [],
          buildingType: ['квартира', 'комната', 'дом'].includes(category) ? buildingType : [],
          stock: ['квартира', 'комната'].includes(category) ? stock : [],
          lotType: ['дом', 'участок'].includes(category) ? lotType : [],
          subCategory: ['дом'].includes(category) ? subCategory : [],
          entrance: ['коммерция'].includes(category) ? entrance : [],
          commercialBuildingType: ['коммерция'].includes(category) ? commercialBuildingType : [],
          location: locationState.region,
          stat: 1,
        }, { signal });

        if (!response.data || response.data.status !== 'ok')
          throw new Error(response.data.text);
  
        setFoundCount(response.data.data.count);
        setIsLoading(false);
      } catch (error) {}
    };
    fetchData();

    return () => controller.abort();
  }, [locationState, type, category, priceFrom, priceTo, noCommission, exclusive, district, studio, rooms, squareFrom, squareTo, squareAreaFrom, squareAreaTo, commercialType, squareLivingFrom, squareLivingTo, squareKitchenFrom, squareKitchenTo, balcony, ceiling, floorFrom, floorTo, floorsFrom, floorsTo, renovation, buildingType, bathroom, stock, lotType, subCategory, entrance, commercialBuildingType]);

  /** TEMPLATE */
  return (
    <Modal
      title="Фильтры"
      visibility={objectsState.state.isFiltersMobileVisible}
      onClose={() =>
        setObjectsState({
          type: 'SET_STATE',
          payload: { ...objectsState.state, isFiltersMobileVisible: false }
        })
      }
    >
      <Radio
        title="Тип сделки"
        radios={[
          { text: 'Купить', value: 'продажа' },
          { text: 'Снять', value: 'аренда', disabled: category === 'участок' }
        ]}
        selected={type}
        onChange={(v) => setType(v)}
      />
      <Radio
        title="Категория объекта"
        radios={[
          { text: 'Квартира', value: 'квартира' },
          { text: 'Комната', value: 'комната' },
          { text: 'Дом', value: 'дом' },
          { text: 'Участок', value: 'участок', disabled: type === 'аренда' },
          { text: 'Коммерция', value: 'коммерция' }
        ]}
        selected={category}
        onChange={(v) => setCategory(v)}
      />
      <Input
        title="Стоимость"
        inputs={[
          {
            placeholder: 'Цена от',
            mask: 'money',
            value: priceFrom,
            max: 99999999
          },
          {
            placeholder: 'Цена до',
            mask: 'money',
            value: priceTo,
            max: 99999999
          }
        ]}
        onChange={(v) => {
          setPriceFrom(v[0]);
          setPriceTo(v[1]);
        }}
        suffix="₽"
      />
      <Checkbox
        title="Количество комнат"
        checkboxes={[
          { text: 'Студия', value: 'студия' },
          { text: '1К', value: '1' },
          { text: '2К', value: '2' },
          { text: '3К', value: '3' },
          { text: '4К+', value: '4' }
        ]}
        visibility={['квартира'].includes(category)}
        selected={
          studio
            ? ['студия', ...rooms]
            : rooms
        }
        onChange={(v) => {
          if (v.includes('студия')) setStudio(1);
          else setStudio(0);

          setRooms(v.filter((x) => x !== 'студия'));
        }}
      />
      <Input
        title="Общая площадь"
        inputs={[
          {
            placeholder: 'Площадь от',
            mask: 'number',
            value: squareFrom,
            max: 999999
          },
          {
            placeholder: 'Площадь до',
            mask: 'number',
            value: squareTo,
            max: 999999
          }
        ]}
        visibility={['квартира', 'комната', 'дом'].includes(category)}
        suffix="м²"
        onChange={(v) => {
          setSquareFrom(v[0]);
          setSquareTo(v[1]);
        }}
      />
      <Input
        title="Площадь участка"
        inputs={[
          {
            placeholder: 'Площадь от',
            mask: 'number',
            value: squareAreaFrom,
            max: 999999
          },
          {
            placeholder: 'Площадь до',
            mask: 'number',
            value: squareAreaTo,
            max: 999999
          }
        ]}
        visibility={['дом', 'участок'].includes(category)}
        suffix="сот"
        onChange={(v) => {
          setSquareAreaFrom(v[0]);
          setSquareAreaTo(v[1]);
        }}
      />
      <Select
        title="Назначение объекта"
        options={[
          {
            type: 'option',
            text: 'Автосервис',
            value: 'автосервис'
          },
          {
            type: 'option',
            text: 'Гостиница',
            value: 'гостиница'
          },
          {
            type: 'option',
            text: 'Готовый бизнес',
            value: 'готовый бизнес'
          },
          {
            type: 'option',
            text: 'Коммерческая земля',
            value: 'коммерческая земля'
          },
          {
            type: 'option',
            text: 'Общепит',
            value: 'общепит'
          },
          {
            type: 'option',
            text: 'Офисное помещение',
            value: 'офисное помещение'
          },
          {
            type: 'option',
            text: 'ПСН',
            value: 'псн'
          },
          {
            type: 'option',
            text: 'Производственное помещение',
            value: 'производственное помещение'
          },
          {
            type: 'option',
            text: 'Склад',
            value: 'склад'
          },
          {
            type: 'option',
            text: 'Торговое помещение',
            value: 'торговое помещение'
          }
        ]}
        selected={commercialType}
        visibility={['коммерция'].includes(category)}
        multiple
        onChange={(v) => {
          setCommercialType(v);
        }}
      />
      <Checkbox
        title="Акции"
        checkboxes={[
          { text: 'Без комиссии', value: 'Без комиссии' },
          { text: 'Только у Нас', value: 'Только у Нас' }
        ]}
        selected={[
          noCommission && 'Без комиссии',
          exclusive && 'Только у Нас'
        ].filter((x) => x)}
        onChange={(v) => {
          setNoCommission(Number(v.includes('Без комиссии')));
          setExclusive(Number(v.includes('Только у Нас')));
        }}
      />
      <div style={{ borderTop: '1px dashed #e8ebef', margin: '18px -12px ' }} />
      <Input
        title="Жилая площадь"
        inputs={[
          {
            placeholder: 'Площадь от',
            mask: 'number',
            value: squareLivingFrom,
            max: 999999
          },
          {
            placeholder: 'Площадь до',
            mask: 'number',
            value: squareLivingTo,
            max: 999999
          }
        ]}
        visibility={['квартира', 'комната', 'дом'].includes(category)}
        suffix="м²"
        onChange={(v) => {
          setSquareLivingFrom(v[0]);
          setSquareLivingTo(v[1]);
        }}
      />
      <Input
        title="Кухонная площадь"
        inputs={[
          {
            placeholder: 'Площадь от',
            mask: 'number',
            value: squareKitchenFrom,
            max: 999999
          },
          {
            placeholder: 'Площадь до',
            mask: 'number',
            value: squareKitchenTo,
            max: 999999
          }
        ]}
        visibility={['квартира', 'дом'].includes(category)}
        suffix="м²"
        onChange={(v) => {
          setSquareKitchenFrom(v[0]);
          setSquareKitchenTo(v[1]);
        }}
      />
      <Checkbox
        title="Балкон"
        checkboxes={[
          { text: 'Балкон', value: 'балкон' },
          { text: 'Лоджия', value: 'лоджия' }
        ]}
        selected={balcony}
        visibility={['квартира', 'комната'].includes(category)}
        onChange={(v) => {
          setBalcony(v);
        }}
      />
      <Checkbox
        title="Высота потолков"
        checkboxes={[
          { text: 'от 2.5м', value: '2.5' },
          { text: 'от 2.7м', value: '2.7' },
          { text: 'от 3м', value: '3' },
          { text: 'от 4м', value: '4' }
        ]}
        single
        selected={ceiling}
        visibility={['квартира', 'комната', 'дом', 'коммерция'].includes(category)}
        onChange={(v) => {
          setCeiling(v[0]);
        }}
      />
      <Input
        title="Этаж"
        inputs={[
          {
            placeholder: 'Этаж от',
            mask: 'number',
            value: floorFrom,
            max: 99
          },
          {
            placeholder: 'Этаж до',
            mask: 'number',
            value: floorTo,
            max: 99
          }
        ]}
        visibility={['квартира', 'комната', 'коммерция'].includes(category)}
        suffix="эт"
        onChange={(v) => {
          setFloorFrom(v[0]);
          setFloorTo(v[1]);
        }}
      />
      <Input
        title="Этажей"
        inputs={[
          {
            placeholder: 'Этажей от',
            mask: 'number',
            value: floorsFrom,
            max: 999999
          },
          {
            placeholder: 'Этажей до',
            mask: 'number',
            value: floorsTo,
            max: 999999
          }
        ]}
        visibility={['квартира', 'комната', 'дом', 'коммерция'].includes(category)}
        suffix="эт"
        onChange={(v) => {
          setFloorsFrom(v[0]);
          setFloorsTo(v[1]);
        }}
      />
      <Select
        title="Ремонт"
        options={[
          {
            type: 'option',
            text: 'Дизайнерский',
            value: 'дизайнерский'
          },
          {
            type: 'option',
            text: 'Евроремонт',
            value: 'евро'
          },
          {
            type: 'option',
            text: 'Требует ремонта',
            value: 'требует ремонта'
          },
          {
            type: 'option',
            text: 'Хороший',
            value: 'хороший'
          },
          {
            type: 'option',
            text: 'Частичный ремонт',
            value: 'частичный ремонт'
          },
          {
            type: 'option',
            text: 'Черновая отделка',
            value: 'черновая отделка'
          }
        ]}
        selected={renovation}
        visibility={['квартира', 'комната', 'дом', 'коммерция'].includes(category)}
        multiple
        onChange={(v) => {
          setRenovation(v);
        }}
      />
      <Checkbox
        title="Санузел"
        checkboxes={[
          { text: 'Совмещенный', value: 'совмещенный' },
          { text: 'Раздельный', value: 'раздельный' },
          { text: 'Два и более', value: 'два и более' }
        ]}
        selected={bathroom}
        visibility={['квартира', 'комната'].includes(category)}
        onChange={(v) => {
          setBathroom(v);
        }}
      />
      <Select
        title="Материал стен дома"
        options={[
          {
            type: 'option',
            text: 'Блочный',
            value: 'блочный'
          },
          {
            type: 'option',
            text: 'Кирпично-монолитный',
            value: 'кирпично-монолитный'
          },
          {
            type: 'option',
            text: 'Кирпичный',
            value: 'кирпичный'
          },
          {
            type: 'option',
            text: 'Монолит',
            value: 'монолит'
          },
          {
            type: 'option',
            text: 'Панельный',
            value: 'панельный'
          },
          {
            type: 'option',
            text: 'Деревянный',
            value: 'деревянный'
          }
        ]}
        selected={buildingType}
        visibility={['квартира', 'комната', 'дом'].includes(category)}
        multiple
        onChange={(v) => {
          setBuildingType(v);
        }}
      />
      <Select
        title="Фонд здания"
        options={[
          {
            type: 'option',
            text: 'Коттедж',
            value: 'коттедж'
          },
          {
            type: 'option',
            text: 'Новостройка',
            value: 'новостройка'
          },
          {
            type: 'option',
            text: 'Современный',
            value: 'современный'
          },
          {
            type: 'option',
            text: 'Сталинский',
            value: 'сталинский'
          },
          {
            type: 'option',
            text: 'Старый фонд',
            value: 'старый фонд'
          },
          {
            type: 'option',
            text: 'Хрущевка',
            value: 'хрущевка'
          }
        ]}
        selected={stock}
        visibility={['квартира', 'комната'].includes(category)}
        multiple
        onChange={(v) => {
          setStock(v);
        }}
      />
      <Checkbox
        title="Тип участка"
        checkboxes={[
          { text: 'ИЖС', value: 'ижс' },
          { text: 'Садовый', value: 'садовый' },
          { text: 'Фермерский', value: 'фермерский' }
        ]}
        selected={lotType}
        visibility={['дом', 'участок'].includes(category)}
        onChange={(v) => {
          setLotType(v);
        }}
      />
      <Select
        title="Тип дома"
        options={[
          {
            type: 'option',
            text: 'Доля',
            value: 'доля'
          },
          {
            type: 'option',
            text: 'Дуплекс',
            value: 'дуплекс'
          },
          {
            type: 'option',
            text: 'Отдельный дом',
            value: 'отдельный дом'
          },
          {
            type: 'option',
            text: 'Таунхаус',
            value: 'таунхаус'
          },
          {
            type: 'option',
            text: 'Часть дома',
            value: 'часть дома'
          }
        ]}
        selected={subCategory}
        visibility={['дом'].includes(category)}
        multiple
        onChange={(v) => {
          setSubCategory(v);
        }}
      />
      <Checkbox
        title="Тип входа"
        checkboxes={[
          { text: 'Общий', value: 'общий' },
          { text: 'Отдельный', value: 'отдельный' }
        ]}
        selected={entrance}
        visibility={['коммерция'].includes(category)}
        single
        onChange={(v) => {
          setEntrance(v[0]);
        }}
      />
      <Select
        title="Тип здания"
        options={[
          {
            type: 'option',
            text: 'Бизнес-центр',
            value: 'бизнес-центр'
          },
          {
            type: 'option',
            text: 'Встроенное помещение',
            value: 'встроенное помещение'
          },
          {
            type: 'option',
            text: 'Отдельно стоящее здание',
            value: 'отдельно стоящее здание'
          },
          {
            type: 'option',
            text: 'Складской комплекс',
            value: 'складской комплекс'
          },
          {
            type: 'option',
            text: 'Торговый центр',
            value: 'Торговый центр'
          }
        ]}
        selected={commercialBuildingType}
        visibility={['коммерция'].includes(category)}
        multiple
        onChange={(v) => {
          setCommercialBuildingType(v);
        }}
      />
      <div className={css.actions}>
        <Button
          color="red"
          fullWidth
          style={{ marginRight: 8 }}
          visibility={isAnyFilters(objectsState.filter)}
          onClick={handleResetClick}
        >
          Очистить
        </Button>
        <Button
          color="green"
          fullWidth
          onClick={handleSearchClick}
          loading={isLoading}
        >
          {foundCount > 0 ? `Показать ${foundCount} ${declOfNum(foundCount, ['объект', 'объекта', 'объектов'])}` : 'Найти'}
        </Button>
      </div>
    </Modal>
  );
};

export default ObjectsFilterMobile;
